import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import cache from "utils/cache";
import {
  getProductCategorySlug,
  labelForCategorySlug,
  PRODUCT_CATEGORY_TABS,
} from "utils/productCategory";
import {
  ActionRow,
  AccentBar,
  Badge,
  BadgeRow,
  BentoGrid,
  CardContent,
  CardOverlay,
  CategoryChip,
  CategoryChipRow,
  CountPill,
  EmptyGridMessage,
  HeaderLeft,
  HeaderRight,
  ImportLabel,
  ImportLink,
  MainCard,
  MainContainer,
  MetricLabel,
  MetricRow,
  MetricValue,
  SectionHeader,
  SectionTitle,
  SmallCard,
  Stat,
  StatDivider,
  StyledSubTitle,
  StyledTitle,
  SupplierMeta,
  Tab,
  TabsWrap,
  TopRightCard,
  ViewAllLink,
} from "./styles";

const CATEGORY_TABS = PRODUCT_CATEGORY_TABS;
const VIEW_ALL_PRODUCTS_PATH = "/best-selling-products?category=all";

const categoryHref = (slug) =>
  slug === "all" ? VIEW_ALL_PRODUCTS_PATH : `/collection/${slug}`;

const shouldUseNativeNavigation = (event) =>
  Boolean(
    event?.metaKey ||
      event?.ctrlKey ||
      event?.shiftKey ||
      event?.altKey ||
      event?.button === 1
  );

const formatPrice = (value) => {
  const parsed = Number(value || 0);
  return `$${parsed.toFixed(2)}`;
};

const NewArrivalSection = ({
  products = [],
  categorySlug = "all",
  onCategoryChange,
  total = null,
  isLoading = false,
  sectionRef,
}) => {
  const user = cache.getItem("user");

  const normalizedProducts = useMemo(
    () =>
      (products || []).map((product) => {
        const price = Number(product?.price || 0);
        const msrp = Number(product?.MSRP || 0);
        const profit = Math.max(msrp - price, 0);
        const margin = msrp > 0 ? Math.round((profit / msrp) * 100) : 0;
        const categoryTag = getProductCategorySlug(product);
        return {
          ...product,
          categoryTag,
          imageUrl: product?.images?.[0]?.url,
          price,
          msrp,
          profit,
          margin,
        };
      }),
    [products]
  );

  // Parent page loads collection API by slug; grid uses that list directly.
  const displayProducts = normalizedProducts;

  const mainProduct = displayProducts[0];
  const thirdProduct = displayProducts[2];
  const fourthProduct = displayProducts[3];

  const activeChip =
    categorySlug === "all" ? "fashion" : categorySlug;

  const badgeCount =
    typeof total === "number" ? total : displayProducts.length;

  const handleCategorySelect = (slug, event) => {
    if (shouldUseNativeNavigation(event)) return;
    if (typeof onCategoryChange !== "function") return;
    event?.preventDefault?.();
    event?.currentTarget?.blur?.();
    onCategoryChange(slug);
  };

  const handleCategoryMouseDown = (event) => {
    // Avoid focus/scroll jump on primary click; keep middle-click open-in-new-tab working.
    if (event.button === 0) event.preventDefault();
  };

  const renderMetrics = (product, compact = false) => {
    if (!product) return null;
    return (
      <MetricRow>
        <Stat>
          <MetricValue>{formatPrice(product.price)}</MetricValue>
          <MetricLabel>Cost</MetricLabel>
        </Stat>
        <StatDivider />
        <Stat>
          <MetricValue className="blue">{formatPrice(product.msrp)}</MetricValue>
          <MetricLabel>Sell</MetricLabel>
        </Stat>
        <StatDivider />
        <Stat>
          <MetricValue className="green">{formatPrice(product.profit)}</MetricValue>
          <MetricLabel>Profit</MetricLabel>
        </Stat>
        {!compact && (
          <>
            <StatDivider />
            <Stat>
              <MetricValue className="green">{`${product.margin || 0}%`}</MetricValue>
              <MetricLabel>Margin</MetricLabel>
            </Stat>
          </>
        )}
      </MetricRow>
    );
  };

  const hasProducts = displayProducts.length > 0;
  const mainProductPath = mainProduct?.id ? `/product/${mainProduct.id}` : undefined;

  return (
    <MainContainer ref={sectionRef}>
      <SectionHeader>
        <HeaderLeft>
          <AccentBar />
          <SectionTitle>New Arrivals & Featured</SectionTitle>
        </HeaderLeft>
        <HeaderRight>
          <TabsWrap>
            {CATEGORY_TABS.map((tab) => (
              <Tab
                key={tab}
                to={categoryHref(tab)}
                className={categorySlug === tab ? "active" : ""}
                onMouseDown={handleCategoryMouseDown}
                onClick={(e) => {
                  if (isLoading) {
                    e.preventDefault();
                    return;
                  }
                  handleCategorySelect(tab, e);
                }}
                aria-disabled={isLoading || undefined}
              >
                {labelForCategorySlug(tab)}
              </Tab>
            ))}
          </TabsWrap>
          <ViewAllLink
            to={VIEW_ALL_PRODUCTS_PATH}
            state={{ refreshCatalog: true }}
          >
            View all products -&gt;
          </ViewAllLink>
        </HeaderRight>
      </SectionHeader>

      {!hasProducts && !isLoading ? (
        <EmptyGridMessage>
          No products found in {labelForCategorySlug(categorySlug)}. Try another category
          or browse all products.
        </EmptyGridMessage>
      ) : (
        <BentoGrid>
          <MainCard
            as={mainProductPath ? Link : "article"}
            to={mainProductPath}
            $image={mainProduct?.imageUrl}
          >
            <CardOverlay />
            <CardContent>
              <BadgeRow>
                <Badge className="new">New This Week</Badge>
                <Badge className="hot">Top Seller</Badge>
                <Badge>
                  {mainProduct?.categoryTag
                    ? labelForCategorySlug(mainProduct.categoryTag)
                    : "Featured"}
                </Badge>
              </BadgeRow>
              <StyledTitle className="lg">
                {mainProduct?.name || "No product in this category"}
              </StyledTitle>
              <StyledSubTitle>
                {mainProduct
                  ? (mainProduct.description ||
                    "Best-performing product with fast shipping and high demand."
                  ).slice(0, 110)
                  : "Select another category to browse featured items."}
              </StyledSubTitle>
              {renderMetrics(mainProduct)}
              <SupplierMeta>
                Supplier reliability: <span>{user ? "Verified" : "Login to view"}</span>
              </SupplierMeta>
              {mainProduct && (
                <ActionRow>
                  <ImportLabel>View Details</ImportLabel>
                </ActionRow>
              )}
            </CardContent>
          </MainCard>

          <TopRightCard>
            <CardOverlay />
            <CardContent>
              <CountPill>{badgeCount}+ products available</CountPill>
              <StyledTitle className="md">Featured Category</StyledTitle>
              <StyledSubTitle className="md">
                Browse what&apos;s selling best right now across all 5 marketplaces.
              </StyledSubTitle>
              <CategoryChipRow>
                {CATEGORY_TABS.filter((t) => t !== "all").map((cat) => (
                  <CategoryChip
                    key={cat}
                    to={categoryHref(cat)}
                    className={activeChip === cat ? "active" : ""}
                    onMouseDown={handleCategoryMouseDown}
                    onClick={(e) => handleCategorySelect(cat, e)}
                  >
                    {labelForCategorySlug(cat)}
                  </CategoryChip>
                ))}
              </CategoryChipRow>
              <ImportLink className="sm" to={`/collection/${activeChip}`}>
                {`Browse ${labelForCategorySlug(activeChip)} ->`}
              </ImportLink>
            </CardContent>
          </TopRightCard>

          {[thirdProduct, fourthProduct].map((product, idx) => {
            const productPath = product?.id ? `/product/${product.id}` : undefined;
            return (
              <SmallCard
                key={product?.id || `slot-${idx}`}
                as={productPath ? Link : "article"}
                to={productPath}
                $image={product?.imageUrl}
              >
                <CardOverlay />
                <CardContent>
                  <BadgeRow>
                    <Badge className={idx === 0 ? "trend" : "new"}>
                      {idx === 0 ? "Trending" : "New"}
                    </Badge>
                    <Badge>
                      {product?.categoryTag
                        ? labelForCategorySlug(product.categoryTag)
                        : "Category"}
                    </Badge>
                  </BadgeRow>
                  <StyledTitle className="sm">{product?.name || "Product"}</StyledTitle>
                  <StyledSubTitle className="sm">
                    {(product?.description ||
                      "High-converting product with strong supplier performance."
                    ).slice(0, 80)}
                  </StyledSubTitle>
                  {renderMetrics(product, true)}
                  {productPath && (
                    <ImportLabel className="sm">View Details</ImportLabel>
                  )}
                </CardContent>
              </SmallCard>
            );
          })}
        </BentoGrid>
      )}
    </MainContainer>
  );
};

export default NewArrivalSection;
